import React, { useState, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Eye, Heart, Sparkles, Info, Quote, Plus, Trash2, Lightbulb } from 'lucide-react';
import { tokenizeAll, experientialWords, emotionWords, splitIntoSentences, countWordMatches } from './qualitativeUtils';

const PhenomenologicalAnalysis = ({ texts, surveyId }) => {
  const [essences, setEssences] = useState(() => {
    const saved = localStorage.getItem(`phenom-essences-${surveyId}`);
    return saved ? JSON.parse(saved) : [];
  });
  
  const [selectedQuotes, setSelectedQuotes] = useState(() => {
    const saved = localStorage.getItem(`phenom-quotes-${surveyId}`);
    return saved ? JSON.parse(saved) : [];
  });

  const [newEssence, setNewEssence] = useState('');

  const saveEssences = (e) => { setEssences(e); localStorage.setItem(`phenom-essences-${surveyId}`, JSON.stringify(e)); };
  const saveQuotes = (q) => { setSelectedQuotes(q); localStorage.setItem(`phenom-quotes-${surveyId}`, JSON.stringify(q)); };

  const analysis = useMemo(() => {
    const experientialResponses = [];
    const emotionalResponses = [];
    const emotionCounts = {};

    texts.forEach((text, idx) => {
      const words = tokenizeAll(text);
      const expCount = countWordMatches(words, experientialWords);
      const emoCount = countWordMatches(words, emotionWords);

      if (expCount > 0) {
        experientialResponses.push({
          text,
          index: idx,
          expCount,
          expWords: words.filter(w => experientialWords.has(w))
        });
      }

      if (emoCount > 0) {
        emotionalResponses.push({
          text,
          index: idx,
          emoCount,
          emoWords: words.filter(w => emotionWords.has(w))
        });
        
        words.filter(w => emotionWords.has(w)).forEach(word => {
          emotionCounts[word] = (emotionCounts[word] || 0) + 1;
        });
      }
    });

    const topEmotions = Object.entries(emotionCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([emotion, count]) => ({ emotion, count }));

    const sentences = texts.flatMap(text => splitIntoSentences(text));
    const experientialSentences = sentences.filter(s => {
      const words = tokenizeAll(s);
      return countWordMatches(words, experientialWords) > 0;
    });

    const livedExperienceIndicators = texts.filter(text => {
      const lower = text.toLowerCase();
      return lower.includes('felt like') || 
             lower.includes('experienced') || 
             lower.includes('it was like') ||
             lower.includes('seemed to') ||
             lower.includes('made me feel') ||
             lower.includes('i remember') ||
             lower.includes('i noticed');
    });

    return {
      totalResponses: texts.length,
      experientialCount: experientialResponses.length,
      experientialPercentage: texts.length > 0 ? Math.round((experientialResponses.length / texts.length) * 100) : 0,
      emotionalCount: emotionalResponses.length,
      emotionCount: Object.keys(emotionCounts).length,
      topEmotions,
      experientialResponses: experientialResponses.sort((a, b) => b.expCount - a.expCount),
      emotionalResponses: emotionalResponses.sort((a, b) => b.emoCount - a.emoCount),
      experientialSentences: experientialSentences.slice(0, 20),
      livedExperienceCount: livedExperienceIndicators.length,
      essenceCount: essences.length
    };
  }, [texts, essences]);

  const phenomenologicalSummary = useMemo(() => {
    const topEmotion = analysis.topEmotions[0];
    const secondEmotion = analysis.topEmotions[1];
    const thirdEmotion = analysis.topEmotions[2];
    const emotionList = [topEmotion?.emotion, secondEmotion?.emotion, thirdEmotion?.emotion].filter(Boolean).join(', ');
    
    const experientialLevel = analysis.experientialPercentage > 50 
      ? 'rich phenomenological data capturing personal experience' 
      : 'moderate levels of experiential expression';
    
    const emotionInfo = topEmotion 
      ? ` (${topEmotion.emotion}: ${topEmotion.count} occurrences)` 
      : '';
    
    const essenceInfo = essences.length > 0 
      ? `${essences.length} essence themes have been identified to capture the fundamental nature of participant experiences.` 
      : 'Researchers should identify essence themes that capture the fundamental structure of participant experiences.';
    
    return `This phenomenological analysis explored the lived experiences expressed across ${analysis.totalResponses} text segments, identifying experiential and emotional content that reveals participant subjective worlds. Experiential language (verbs of perception, sensing, and being) was present in ${analysis.experientialPercentage}% of responses (${analysis.experientialCount} segments), indicating ${experientialLevel}. ${analysis.emotionCount} distinct emotional expressions were identified, with the most frequent emotions being ${emotionList || 'various emotional states'}${emotionInfo}. ${analysis.livedExperienceCount} responses contained explicit lived experience markers such as "felt like," "experienced," or "made me feel." ${essenceInfo} The phenomenological approach requires bracketing researcher assumptions and focusing on how phenomena appear to participants. Consider triangulating these experiential insights with narrative analysis to understand how experiences unfold over time.`;
  }, [analysis, essences]);

  const addEssence = () => {
    if (newEssence.trim()) {
      const essence = { 
        id: Date.now().toString(), 
        name: newEssence.trim(),
        quotes: []
      };
      saveEssences([...essences, essence]);
      setNewEssence('');
    }
  };

  const removeEssence = (id) => {
    saveEssences(essences.filter(e => e.id !== id));
  };

  const toggleQuoteForEssence = (essenceId, quote) => {
    const updated = essences.map(e => {
      if (e.id === essenceId) {
        const hasQuote = e.quotes.some(q => q.text === quote.text);
        return {
          ...e,
          quotes: hasQuote 
            ? e.quotes.filter(q => q.text !== quote.text)
            : [...e.quotes, quote]
        };
      }
      return e;
    });
    saveEssences(updated);
  };

  const addToSelectedQuotes = (quote) => {
    if (!selectedQuotes.some(q => q.text === quote.text)) {
      saveQuotes([...selectedQuotes, { ...quote, addedAt: new Date().toISOString() }]);
    }
  };

  const removeFromSelectedQuotes = (text) => {
    saveQuotes(selectedQuotes.filter(q => q.text !== text));
  };

  return (
    <div className="phenomenological-analysis">
      <div className="analysis-header">
        <h4><Eye size={20} /> Phenomenological Analysis</h4>
        <p>Explore lived experiences, feelings, and essence themes in responses</p>
      </div>

      <div className="analysis-guidance">
        <Lightbulb size={18} />
        <p>Here are some charts/graphs and some analytical content to get you started. You will need to expand on these and possibly make connections and triangulation with other data analysis.</p>
      </div>

      <div className="stats-row">
        <div className="stat-box">
          <div className="stat-value">{analysis.totalResponses}</div>
          <div className="stat-label">Responses Analyzed</div>
        </div>
        <div className="stat-box highlight">
          <div className="stat-value">{analysis.experientialPercentage}%</div>
          <div className="stat-label">Experiential Content</div>
        </div>
        <div className="stat-box">
          <div className="stat-value">{analysis.emotionCount}</div>
          <div className="stat-label">Distinct Emotions</div>
        </div>
        <div className="stat-box">
          <div className="stat-value">{analysis.livedExperienceCount}</div>
          <div className="stat-label">Lived Experience Markers</div>
        </div>
      </div>

      <div className="phenom-sections">
        <div className="section experiential-section">
          <h5><Sparkles size={16} /> Experiential Statements</h5>
          <p className="section-desc">Responses containing verbs of perception, sensing, and being</p>
          
          <div className="experiential-list">
            {analysis.experientialResponses.slice(0, 8).map((resp, idx) => (
              <div key={idx} className="experiential-card">
                <div className="exp-words">
                  {resp.expWords.slice(0, 5).map((w, i) => (
                    <span key={i} className="exp-word">{w}</span>
                  ))}
                </div>
                <p className="exp-text">
                  "{resp.text.substring(0, 180)}{resp.text.length > 180 ? '...' : ''}"
                </p>
                <button 
                  className="add-quote-btn"
                  onClick={() => addToSelectedQuotes({ text: resp.text, type: 'experiential' })}
                >
                  <Quote size={14} /> Save Quote
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="section emotions-section">
          <h5><Heart size={16} /> Emotional Expression</h5>
          
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={analysis.topEmotions} layout="vertical" margin={{ left: 80, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis type="category" dataKey="emotion" width={80} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#ec4899" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="emotional-responses">
            {analysis.emotionalResponses.slice(0, 4).map((resp, idx) => (
              <div key={idx} className="emotion-card">
                <div className="emo-words">
                  {resp.emoWords.slice(0, 3).map((w, i) => (
                    <span key={i} className="emo-word">{w}</span>
                  ))}
                </div>
                <p>"{resp.text.substring(0, 150)}..."</p>
                <button 
                  className="add-quote-btn"
                  onClick={() => addToSelectedQuotes({ text: resp.text, type: 'emotional' })}
                >
                  <Quote size={14} /> Save Quote
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="essence-themes">
        <h5>Essence Themes</h5>
        <p className="section-desc">
          Identify core themes that capture the essential meaning of participants' experiences
        </p>
        
        <div className="add-essence-form">
          <input
            type="text"
            placeholder="Name a new essence theme..."
            value={newEssence}
            onChange={e => setNewEssence(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addEssence()}
          />
          <button onClick={addEssence}><Plus size={16} /> Add Theme</button>
        </div>

        <div className="essences-list">
          {essences.map(essence => (
            <div key={essence.id} className="essence-card">
              <div className="essence-header">
                <span className="essence-name">{essence.name}</span>
                <span className="quote-count">{essence.quotes.length} quotes</span>
                <button onClick={() => removeEssence(essence.id)}><Trash2 size={14} /></button>
              </div>
              <div className="essence-quotes">
                {essence.quotes.map((q, idx) => (
                  <div key={idx} className="essence-quote">
                    <Quote size={12} />
                    <span>"{q.text.substring(0, 100)}..."</span>
                  </div>
                ))}
              </div>
              <div className="assign-quotes">
                <p>Assign quotes from saved collection:</p>
                <div className="assignable-quotes">
                  {selectedQuotes.slice(0, 5).map((q, idx) => (
                    <button
                      key={idx}
                      className={essence.quotes.some(eq => eq.text === q.text) ? 'assigned' : ''}
                      onClick={() => toggleQuoteForEssence(essence.id, q)}
                    >
                      {q.text.substring(0, 30)}...
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ))}
          {essences.length === 0 && (
            <div className="empty-state">
              <Eye size={32} />
              <p>Create essence themes to organize the core meanings from responses</p>
            </div>
          )}
        </div>
      </div>

      <div className="saved-quotes">
        <h5><Quote size={16} /> Saved Quotes ({selectedQuotes.length})</h5>
        <div className="quotes-grid">
          {selectedQuotes.map((q, idx) => (
            <div key={idx} className={`saved-quote ${q.type}`}>
              <span className="quote-type">{q.type}</span>
              <p>"{q.text.substring(0, 120)}..."</p>
              <button onClick={() => removeFromSelectedQuotes(q.text)}>
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="interpretation-section">
        <h5><Info size={18} /> Analytical Summary</h5>
        <p>{phenomenologicalSummary}</p>
      </div>
    </div>
  );
};

export default PhenomenologicalAnalysis;
